import { financeCarryRows, financeExplorerColumns, type FinanceCarryRow, type FinanceCarryValuesSchema, type FinanceComparisonProjection, type FinanceExplorerValues } from "@chro/shared";
import type { z } from "zod";

/** Presentation geometry for the Finance bridge charts. Every amount drawn here
 * is an already projected Finance value; no recognition or rounding policy lives here. */
export type FinanceWaterfallKey = keyof FinanceExplorerValues;
export type FinanceWaterfallRole = "balance" | "step" | "subtotal" | "total";
export type FinanceWaterfallBridgeId = "vm" | "carry";
export type FinanceWaterfallLabels = Partial<Record<FinanceWaterfallKey, string>>;
export interface FinanceWaterfallStep {
  key: FinanceWaterfallKey;
  label: string;
  value: number | null;
  role: FinanceWaterfallRole;
  /** +1 adds the signed amount to the running balance; −1 subtracts it ("less …"). */
  sign: 1 | -1;
}
export interface FinanceWaterfallBridge { id: FinanceWaterfallBridgeId; steps: FinanceWaterfallStep[] }
export type FinanceWaterfallTone = "add" | "reduce" | "balance" | "flat" | "unavailable";
export interface FinanceWaterfallBar {
  step: FinanceWaterfallStep;
  index: number;
  /** Running balance before and after the bar. An unavailable bar leaves it unchanged. */
  start: number;
  end: number;
  /** Signed change in the running balance, or the level for anchored bars; null when unavailable. */
  movement: number | null;
  tone: FinanceWaterfallTone;
  available: boolean;
}
export interface FinanceWaterfallGeometry { bars: FinanceWaterfallBar[]; min: number; max: number }
export interface FinanceWaterfallLayout {
  width: number; chartHeight: number; paddingTop: number; paddingBottom: number;
  /** Bar width as a share of the column, capped in pixels. */
  barRatio: number; maxBarWidth: number;
}
export interface FinanceWaterfallPlottedBar extends FinanceWaterfallBar {
  x: number; column: number; barWidth: number; yStart: number; yEnd: number; top: number; height: number;
  /** Where the amount label sits: above the bar's top edge or below its bottom edge. */
  labelY: number; placement: "above" | "below";
  /** Hairline from this bar's end to the next bar, or null beside an unavailable bar or at the end. */
  connector: { x1: number; x2: number; y: number } | null;
}
export interface FinanceWaterfallPlot { bars: FinanceWaterfallPlottedBar[]; column: number; barWidth: number; zeroY: number; min: number; max: number }

export const financeWaterfallLabels: Record<FinanceWaterfallKey, string> = Object.fromEntries(
  financeExplorerColumns.map(column => [column.key, column.label]),
) as Record<FinanceWaterfallKey, string>;
export const financeWaterfallTitles: Record<FinanceWaterfallBridgeId, string> = {
  vm: "From calculated VM to recognized P&L", carry: "From opening carry to closing carry",
};
const cents = (value: number) => Math.round(value * 100) / 100;

/** Build the two bridges. Optional adjustments stay visible unless they are a known zero. */
export function financeWaterfallBridges(values: FinanceExplorerValues, labels: FinanceWaterfallLabels = {}): FinanceWaterfallBridge[] {
  const step = (key: FinanceWaterfallKey, role: FinanceWaterfallRole, sign: 1 | -1 = 1): FinanceWaterfallStep =>
    ({ key, label: labels[key] ?? financeWaterfallLabels[key], value: values[key], role, sign });
  const optional = (key: FinanceWaterfallKey, sign: 1 | -1 = 1): FinanceWaterfallStep[] => values[key] === 0 ? [] : [step(key, "step", sign)];
  return [
    { id: "vm", steps: [
      step("calculatedVm", "balance"), ...optional("vmToMovement", -1), step("currentDeferred", "step", -1),
      step("currentRecognized", "subtotal"), step("releasedCarry", "step"), ...optional("pnlRounding"), step("recognizedPnl", "total"),
    ] },
    { id: "carry", steps: [
      step("openingCarry", "balance"), step("currentDeferred", "step"), step("releasedCarry", "step", -1),
      ...optional("transfers"), ...optional("carryRounding"), step("closingCarry", "total"),
    ] },
  ];
}

/** Running baseline in amount units. Balances, subtotals and totals anchor to
 * zero; steps float from the previous level. A null amount draws as unavailable
 * and never moves the baseline, so it is never mistaken for zero. */
export function financeWaterfallGeometry(steps: FinanceWaterfallStep[]): FinanceWaterfallGeometry {
  let position = 0;
  const bars = steps.map((step, index): FinanceWaterfallBar => {
    if (step.value == null) return { step, index, start: position, end: position, movement: null, tone: "unavailable", available: false };
    const anchored = step.role !== "step";
    const start = anchored ? 0 : position;
    const end = cents(anchored ? step.value : position + step.sign * step.value);
    position = end;
    const movement = cents(end - start);
    const tone: FinanceWaterfallTone = anchored ? "balance" : movement > 0 ? "add" : movement < 0 ? "reduce" : "flat";
    return { step, index, start, end, movement, tone, available: true };
  });
  const extremes = bars.flatMap(bar => [bar.start, bar.end]);
  return { bars, min: Math.min(0, ...extremes), max: Math.max(0, ...extremes) };
}

export const financeWaterfallDefaultLayout: FinanceWaterfallLayout = { width: 960, chartHeight: 180, paddingTop: 24, paddingBottom: 24, barRatio: 0.46, maxBarWidth: 88 };

/** Pixel placement for one bridge. The y scale always includes zero so a
 * balance bar is visibly anchored and signs read from the zero line. */
export function financeWaterfallPlot(steps: FinanceWaterfallStep[], layout: FinanceWaterfallLayout = financeWaterfallDefaultLayout): FinanceWaterfallPlot {
  const geometry = financeWaterfallGeometry(steps);
  const count = Math.max(1, steps.length);
  const column = layout.width / count;
  const barWidth = Math.min(layout.maxBarWidth, column * layout.barRatio);
  const range = geometry.max - geometry.min || 1;
  const inner = Math.max(1, layout.chartHeight - layout.paddingTop - layout.paddingBottom);
  const y = (value: number) => layout.paddingTop + ((geometry.max - value) / range) * inner;
  const xOf = (index: number) => column * index + (column - barWidth) / 2;
  const bars = geometry.bars.map((bar): FinanceWaterfallPlottedBar => {
    const x = xOf(bar.index), yStart = y(bar.start), yEnd = y(bar.end);
    const top = Math.min(yStart, yEnd), height = Math.abs(yEnd - yStart);
    const placement: "above" | "below" = bar.available && bar.end < bar.start ? "below" : "above";
    const next = geometry.bars[bar.index + 1];
    const connector = next && bar.available && next.available ? { x1: x + barWidth, x2: xOf(next.index), y: yEnd } : null;
    return { ...bar, x, column, barWidth, yStart, yEnd, top, height, labelY: placement === "above" ? top : top + height, placement, connector };
  });
  return { bars, column, barWidth, zeroY: y(0), min: geometry.min, max: geometry.max };
}

const amountFormat = new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Same text as the Finance tables: signed, two decimals, the minus sign before the number, — when unavailable. */
export const financeWaterfallAmount = (value: number | null | undefined): string => value == null ? "—" : amountFormat.format(value === 0 ? 0 : value);

type FinanceCarryValues = z.infer<typeof FinanceCarryValuesSchema>;
const carryKeyByWaterfallKey: Record<FinanceWaterfallKey, FinanceCarryRow | null> = {
  openingCarry: "openingCarry", calculatedVm: "statementVm", currentRecognized: "currentRecognized", currentDeferred: "currentDeferred",
  vmToMovement: "unreconciledVm", releasedCarry: "releasedCarry", pnlRounding: "pnlRounding", recognizedPnl: "recognizedPnl",
  transfers: null, carryRounding: "carryRounding", closingCarry: "closingCarry",
};
/** The company carry report starts its VM bridge from statement cash: statement VM
 * less unreconciled VM less the carried movement is the recognized movement.
 * Company-wide carry transfers net to zero, so that step is a known zero here. */
export function financeCarryWaterfallValues(values: FinanceCarryValues): FinanceExplorerValues {
  return { openingCarry: values.openingCarry, calculatedVm: values.statementVm, currentRecognized: values.currentRecognized,
    currentDeferred: values.currentDeferred, vmToMovement: values.unreconciledVm, releasedCarry: values.releasedCarry,
    pnlRounding: values.pnlRounding, recognizedPnl: values.recognizedPnl, transfers: 0, carryRounding: values.carryRounding, closingCarry: values.closingCarry };
}
export const financeCarryWaterfallRow = (key: FinanceWaterfallKey): FinanceCarryRow | null => carryKeyByWaterfallKey[key];
export const financeCarryWaterfallLabels: FinanceWaterfallLabels = Object.fromEntries(
  (Object.entries(carryKeyByWaterfallKey) as [FinanceWaterfallKey, FinanceCarryRow | null][])
    .flatMap(([key, row]) => row ? [[key, financeCarryRows.find(item => item.key === row)!.label]] : []),
);
export const financeCarryWaterfallTitles: Partial<Record<FinanceWaterfallBridgeId, string>> = { vm: "From statement VM to recognized P&L" };

/** Sum displayed cents exactly as the comparison projection does; any missing amount makes the total unavailable. */
const valueKeys = financeExplorerColumns.map(column => column.key);
const sumCents = (values: (number | null)[]): number | null => values.some(value => value === null) ? null : values.reduce<number>((total, value) => total + Math.round(value! * 100), 0) / 100;
const sumValues = (values: FinanceExplorerValues[]): FinanceExplorerValues => Object.fromEntries(valueKeys.map(key => [key, sumCents(values.map(value => value[key]))])) as FinanceExplorerValues;
export const financeWaterfallRangeValues = (months: string[], byMonth: Record<string, FinanceExplorerValues>): FinanceExplorerValues => ({
  ...sumValues(months.map(month => byMonth[month]!)), openingCarry: months.length ? byMonth[months[0]!]!.openingCarry : 0, closingCarry: months.length ? byMonth[months.at(-1)!]!.closingCarry : 0,
});

export interface FinanceBridgeScope { from: string; to: string; books?: (string | null)[]; traders?: (string | null)[]; rowIds?: string[] }
export type FinanceBridgeView = Pick<FinanceComparisonProjection, "months" | "sourceItemsByMonth" | "rows">;
/** Loaded months inside [from, to], in report order. */
export const financeBridgeMonths = (months: string[], scope: { from: string; to: string }): string[] => months.filter(month => month >= scope.from && month <= scope.to);
/** Account and trader choices present in the table's current source scope. */
export function financeBridgeChoices(view: FinanceBridgeView): { books: { value: string | null; label: string }[]; traders: { value: string | null; label: string }[] } {
  const choices = (values: (string | null)[]) => [...new Set(values)].map(value => ({ value, label: value ?? "Unassigned" })).sort((a, b) => a.value === null ? 1 : b.value === null ? -1 : a.label.localeCompare(b.label));
  const items = view.months.flatMap(month => view.sourceItemsByMonth[month] ?? []);
  return { books: choices(items.map(item => item.book)), traders: choices(items.map(item => item.trader)) };
}
/** Scope already allocated source cents to chosen months, accounts, traders and
 * rows. Summing items per month is exactly how the table's totals are built, so a
 * default scope reproduces the footer; opening and closing come from the range
 * boundaries, never from adding monthly balances together. */
export function financeBridgeValuesByMonth(view: FinanceBridgeView, scope: FinanceBridgeScope): { months: string[]; byMonth: Record<string, FinanceExplorerValues>; range: FinanceExplorerValues; rowIds: string[] } {
  const allowed = scope.rowIds === undefined ? null : new Set(view.rows.filter(row => scope.rowIds!.includes(row.id)).flatMap(row => view.months.flatMap(month => row.itemIdsByMonth[month] ?? [])));
  const matches = (item: FinanceBridgeView["sourceItemsByMonth"][string][number]) => (allowed === null || allowed.has(item.id))
    && (!scope.books?.length || scope.books.includes(item.book)) && (!scope.traders?.length || scope.traders.includes(item.trader));
  const itemsByMonth = Object.fromEntries(view.months.map(month => [month, (view.sourceItemsByMonth[month] ?? []).filter(matches)]));
  const byMonth = Object.fromEntries(view.months.map(month => [month, sumValues(itemsByMonth[month]!.map(item => item.displayedValues))]));
  const months = financeBridgeMonths(view.months, scope);
  const contributing = new Set(months.flatMap(month => itemsByMonth[month]!.map(item => item.id)));
  const rowIds = view.rows.filter(row => months.some(month => (row.itemIdsByMonth[month] ?? []).some(id => contributing.has(id)))).map(row => row.id);
  return { months, byMonth, range: financeWaterfallRangeValues(months, byMonth), rowIds };
}

/** Company carry values over a month range: flows summed, opening from the first month, closing from the last. */
export function financeCarryRangeValues(months: FinanceCarryValues[]): FinanceCarryValues {
  const keys = Object.keys(months[0] ?? {}) as (keyof FinanceCarryValues)[];
  const summed = Object.fromEntries(keys.map(key => [key, sumCents(months.map(month => month[key]))])) as FinanceCarryValues;
  // An unavailable boundary balance stays unavailable; only an empty range defaults to zero.
  return { ...summed, openingCarry: months.length ? months[0]!.openingCarry : 0, closingCarry: months.length ? months.at(-1)!.closingCarry : 0 };
}
